import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as Plotly from 'plotly.js-dist';
import { debounce } from 'lodash';
import "../styles/testchart.css";

const TestChart = ({
  chartID = "newChart",
  shouldDisplay = true,
  datasets = [],
  viewTimeRange = null
}) => {
  const chartRef = useRef(null);
  const plotlyRef = useRef(null);
  const dropdownRef = useRef(null);
  const virtualListRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [dataVisible, setDataVisible] = useState(true);
  const [pointSize, setPointSize] = useState(3);
  const [decimation, setDecimation] = useState(true);
  const [thinningFactor, setThinningFactor] = useState('auto');
  const [selectedDatasets, setSelectedDatasets] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [legendScrollTop, setLegendScrollTop] = useState(0);
  
  // Virtual list settings
  const ITEM_HEIGHT = 32;
  const VISIBLE_ITEMS = 10;
  const DROPDOWN_HEIGHT = VISIBLE_ITEMS * ITEM_HEIGHT;

  // Initialize selected datasets
  useEffect(() => {
    const initialSelections = {};
    datasets.forEach(dataset => {
      initialSelections[dataset.label] = true;
    });
    setSelectedDatasets(initialSelections);
  }, [datasets]);

  // Filtered datasets for search
  const filteredDatasets = useMemo(() => {
    if (!searchTerm) return datasets;
    return datasets.filter(dataset => 
      dataset.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [datasets, searchTerm]);

  // Virtual list calculations
  const virtualListData = useMemo(() => {
    const startIndex = Math.floor(legendScrollTop / ITEM_HEIGHT);
    const endIndex = Math.min(startIndex + VISIBLE_ITEMS + 2, filteredDatasets.length);
    const visibleDatasets = filteredDatasets.slice(startIndex, endIndex);
    
    return {
      totalHeight: filteredDatasets.length * ITEM_HEIGHT,
      startIndex,
      visibleDatasets,
      offsetY: startIndex * ITEM_HEIGHT
    };
  }, [filteredDatasets, legendScrollTop]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Event handlers
  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);
  
  const resetZoom = useCallback((e) => {
    e.preventDefault();
    if (plotlyRef.current) {
      Plotly.relayout(plotlyRef.current, {
        'xaxis.autorange': true,
        'yaxis.autorange': true
      });
    }
  }, []);
  
  const toggleDataset = useCallback((label) => {
    setSelectedDatasets(prev => ({ ...prev, [label]: !prev[label] }));
  }, []);
  
  const toggleAllDatasets = useCallback((selectAll) => {
    const newSelections = {};
    datasets.forEach(dataset => {
      newSelections[dataset.label] = selectAll;
    });
    setSelectedDatasets(newSelections);
  }, [datasets]);
  
  const handlePointSizeChange = (e) => setPointSize(Number(e.target.value));
  const handleDecimationChange = (e) => setDecimation(e.target.checked);
  const handleThinningFactorChange = (e) => setThinningFactor(e.target.value);
  const handleDataVisibilityToggle = () => setDataVisible(prev => !prev);
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const selectedCount = Object.values(selectedDatasets).filter(Boolean).length;
  const totalCount = datasets.length;

  // Optimized Min-Max Thinning Algorithm
  const minMaxThinning = useCallback((data, bucketSize) => {
    if (!data || data.length < bucketSize) return data;
    const thinned = [];
    const buckets = Math.ceil(data.length / bucketSize);
    
    for (let i = 0; i < buckets; i++) {
      const start = i * bucketSize;
      const end = Math.min(start + bucketSize, data.length);
      const bucket = data.slice(start, end);
      
      if (bucket.length === 0) continue;
      
      let minPoint = bucket[0];
      let maxPoint = bucket[0];
      
      for (let j = 1; j < bucket.length; j++) {
        if (bucket[j].y < minPoint.y) minPoint = bucket[j];
        if (bucket[j].y > maxPoint.y) maxPoint = bucket[j];
      }
      
      if (minPoint !== maxPoint) {
        if (minPoint.x < maxPoint.x) {
          thinned.push(minPoint, maxPoint);
        } else {
          thinned.push(maxPoint, minPoint);
        }
      } else {
        thinned.push(minPoint);
      }
    }
    return thinned;
  }, []);

  // Apply Data Thinning with performance optimization
  const applyDataThinning = useCallback((originalDatasets) => {
    if (!decimation || thinningFactor === 'auto' || !originalDatasets?.length) {
      return originalDatasets;
    }
    
    const factor = parseInt(thinningFactor);
    if (isNaN(factor) || factor <= 1) return originalDatasets;
    
    return originalDatasets.map(dataset => {
      if (!dataset.data?.length || !selectedDatasets[dataset.label]) {
        return { ...dataset, visible: false };
      }
      
      const thinnedData = minMaxThinning(dataset.data, factor);
      return { ...dataset, data: thinnedData, visible: true };
    });
  }, [decimation, thinningFactor, selectedDatasets, minMaxThinning]);

  // Process datasets for Plotly format
  const processDatasets = useCallback((originalDatasets) => {
    if (!originalDatasets?.length) return [];
    
    return originalDatasets.map(dataset => {
      if (!dataset?.data) {
        return { ...dataset, data: [], visible: false };
      }
      
      const processedData = dataset.data
        .filter(point => point?.x !== undefined && point?.y !== undefined)
        .map(point => {
          try {
            const dateValue = point.x instanceof Date ? point.x : new Date(point.x);
            if (isNaN(dateValue.getTime())) return null;
            
            const yValue = typeof point.y === 'string' ? parseFloat(point.y) : point.y;
            if (isNaN(yValue)) return null;
            
            return { x: dateValue, y: yValue };
          } catch {
            return null;
          }
        })
        .filter(Boolean);

      return {
        ...dataset,
        data: processedData,
        visible: selectedDatasets[dataset.label]
      };
    });
  }, [selectedDatasets]);

  // Convert datasets to Plotly traces
  const createPlotlyTraces = useCallback((processedDatasets) => {
    const thinnedDatasets = applyDataThinning(processedDatasets);
    
    return thinnedDatasets
      .filter(dataset => selectedDatasets[dataset.label])
      .map((dataset, index) => {
        const primaryColor = dataset.borderColor || dataset.backgroundColor || `hsl(${(index * 137.5) % 360}, 70%, 50%)`;
        
        const xValues = dataset.data.map(point => point.x);
        const yValues = dataset.data.map(point => point.y);
        
        return {
          x: xValues,
          y: yValues,
          type: 'scattergl',
          mode: dataVisible ? 'lines+markers' : 'lines',
          name: dataset.label,
          line: {
            color: primaryColor,
            width: 2
          },
          marker: {
            color: primaryColor,
            size: dataVisible ? pointSize : 0,
            line: {
              color: 'rgba(0, 0, 0, 0.5)',
              width: 0.5
            }
          },
          hovertemplate: 
            '<b>%{fullData.name}</b><br>' +
            'Time: %{x}<br>' +
            'Value: %{y:.4f}<br>' +
            '<extra></extra>',
          visible: selectedDatasets[dataset.label]
        };
      });
  }, [selectedDatasets, dataVisible, pointSize, applyDataThinning]);

  // Update Chart with Plotly
  const updateChart = useCallback(debounce(async () => {
    if (!shouldDisplay || !chartRef.current) return;
    setLoading(true);
    
    try {
      const processedDatasets = processDatasets(datasets);
      const traces = createPlotlyTraces(processedDatasets);
      
      if (traces.length === 0) {
        setLoading(false);
        return;
      }

      const layout = {
        xaxis: {
          title: 'Time',
          type: 'date',
          tickformat: '%Y-%m-%d %H:%M:%S',
          gridcolor: 'rgba(0, 0, 0, 0.1)',
          tickfont: { size: 11 },
          titlefont: { size: 12 }
        },
        yaxis: {
          title: 'Value',
          gridcolor: 'rgba(0, 0, 0, 0.1)',
          tickfont: { size: 11 },
          titlefont: { size: 12 }
        },
        hovermode: 'closest',
        showlegend: false,
        margin: { l: 60, r: 30, t: 50, b: 60 },
        plot_bgcolor: 'white',
        paper_bgcolor: 'white',
        font: {
          family: 'Arial, sans-serif',
          size: 12
        }
      };

      if (viewTimeRange) {
        try {
          const startDate = new Date(viewTimeRange.start);
          const endDate = new Date(viewTimeRange.end);
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            layout.xaxis.range = [startDate.toISOString(), endDate.toISOString()];
          }
        } catch (err) {
          console.error("Error setting time range:", err);
        }
      }

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d', 'autoScale2d'],
        modeBarButtonsToAdd: [{
          name: 'Reset Zoom',
          icon: Plotly.Icons.home,
          click: function(gd) {
            Plotly.relayout(gd, {
              'xaxis.autorange': true,
              'yaxis.autorange': true
            });
          }
        }],
        responsive: true,
        scrollZoom: true,
        doubleClick: 'reset+autosize',
        displaylogo: false  
      };

      if (plotlyRef.current) {
        await Plotly.react(plotlyRef.current, traces, layout, config);
      } else {
        plotlyRef.current = await Plotly.newPlot(chartRef.current, traces, layout, config);
      }
    } catch (err) {
      console.error("Error creating chart:", err);
    } finally {
      setLoading(false);
    }
  }, 300), [shouldDisplay, datasets, selectedDatasets, pointSize, dataVisible, decimation, thinningFactor, viewTimeRange, processDatasets, createPlotlyTraces]);

  useEffect(() => {
    updateChart();
    return () => {
      if (plotlyRef.current) {
        Plotly.purge(plotlyRef.current);
        plotlyRef.current = null;
      }
      updateChart.cancel();
    };
  }, [updateChart]);

  const handleVirtualScroll = (e) => {
    setLegendScrollTop(e.target.scrollTop);
  };

  if (!shouldDisplay) return null;

  return (
    <div className={`chart-wrapper ${isFullscreen ? 'chart-fullscreen' : ''}`}>
      <div className="chart-header">
        <h5 className="chart-title">Data Visualization ({datasets.length} sources)</h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={toggleFullscreen}>
          <i className="material-icons" style={{ fontSize: '16px' }}>
            {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
          </i>
        </button>
      </div>

      <div className="chart-controls">
        <div className="controls-row primary-controls">
          <button className="btn btn-primary" onClick={resetZoom}>
            <i className="material-icons">zoom_out_map</i>
            Reset Zoom
          </button>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="showDataPoints"
              checked={dataVisible}
              onChange={handleDataVisibilityToggle}
            />
            <label className="form-check-label" htmlFor="showDataPoints">
              Show Data Points
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="enableDecimation"
              checked={decimation}
              onChange={handleDecimationChange}
            />
            <label className="form-check-label" htmlFor="enableDecimation">
              Data Reduction
            </label>
          </div>
        </div>
        <div className="controls-row secondary-controls">
          <div className="control-group">
            <label className="control-label" htmlFor="pointSize">Point Size</label>
            <div className="input-group">
              <input
                type="range"
                className="form-range form-control"
                id="pointSize"
                min="0"
                max="10"
                step="1"
                value={pointSize}
                onChange={handlePointSizeChange}
                disabled={!dataVisible}
              />
              <span className="input-group-text">{pointSize}</span>
            </div>
          </div>
          <div className="control-group">
            <label className="control-label" htmlFor="thinningFactor">Sampling Rate</label>
            <select
              className="form-select"
              id="thinningFactor"
              value={thinningFactor}
              onChange={handleThinningFactorChange}
              disabled={!decimation}
            >
              <option value="auto">Auto (Recommended)</option>
              <option value="2">Every 2nd Point</option>
              <option value="5">Every 5th Point</option>
              <option value="10">Every 10th Point</option>
              <option value="20">Every 20th Point</option>
              <option value="50">Every 50th Point</option>
              <option value="100">Every 100th Point</option>
            </select>
          </div>
        </div>
      </div>

      {datasets && datasets.length > 0 && (
        <div className="dataset-selection">
          <div className="dropdown" ref={dropdownRef}>
            <button
              className="btn btn-outline-secondary dropdown-toggle"
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
            >
              <i className="material-icons">visibility</i>
              Data Sources ({selectedCount}/{totalCount})
            </button>
            {dropdownOpen && (
              <div className="dropdown-menu show" style={{ width: '400px', maxHeight: 'none' }}>
                <div className="dropdown-header">
                  <span>Select Data Sources</span>
                  <div className="header-buttons">
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleAllDatasets(true);
                      }}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleAllDatasets(false);
                      }}
                    >
                      None
                    </button>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <div className="search-container" style={{ padding: '8px 12px' }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search data sources..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{ fontSize: '12px' }}
                  />
                </div>
                <div className="dropdown-divider"></div>
                <div 
                  className="virtual-list-container"
                  style={{ 
                    height: `${DROPDOWN_HEIGHT}px`, 
                    overflowY: 'auto',
                    position: 'relative'
                  }}
                  onScroll={handleVirtualScroll}
                  ref={virtualListRef}
                >
                  <div style={{ height: `${virtualListData.totalHeight}px`, position: 'relative' }}>
                    <div 
                      style={{ 
                        transform: `translateY(${virtualListData.offsetY}px)`,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0
                      }}
                    >
                      {virtualListData.visibleDatasets.map((dataset, index) => {
                        const actualIndex = virtualListData.startIndex + index;
                        const displayColor = dataset.borderColor || dataset.backgroundColor || '#007bff';
                        
                        return (
                          <div 
                            key={`${dataset.label}-${actualIndex}`} 
                            className="dropdown-item-text"
                            style={{ 
                              height: `${ITEM_HEIGHT}px`,
                              display: 'flex',
                              alignItems: 'center',
                              padding: '4px 12px'
                            }}
                          >
                            <div className="form-check" style={{ margin: 0, width: '100%' }}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`dropdown-dataset-${actualIndex}`}
                                checked={selectedDatasets[dataset.label] || false}
                                onChange={() => toggleDataset(dataset.label)}
                                style={{ marginTop: '2px' }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`dropdown-dataset-${actualIndex}`}
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  width: '100%',
                                  overflow: 'hidden'
                                }}
                              >
                                <span
                                  className="dataset-color-indicator"
                                  style={{ 
                                    backgroundColor: displayColor,
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '2px',
                                    marginRight: '8px',
                                    flexShrink: 0
                                  }}
                                ></span>
                                <span 
                                  className="dataset-label" 
                                  title={dataset.label}
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {dataset.label}
                                </span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {filteredDatasets.length === 0 && (
                  <div className="dropdown-item-text text-muted text-center" style={{ padding: '20px' }}>
                    No data sources found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {viewTimeRange && (
        <div className="date-range-info">
          <div className="range-badge">
            <i className="material-icons">date_range</i>
            <span>
              {new Date(viewTimeRange.start).toLocaleDateString()} -{' '}
              {new Date(viewTimeRange.end).toLocaleDateString()}
            </span>
          </div>
        </div>
      )}

      <div className="chart-container" style={{ height: isFullscreen ? '80vh' : '60vh' }}>
        {loading && (
          <div className="chart-loading-overlay">
            <div className="loading-content">
              <div className="spinner-border" role="status"></div>
              <span className="loading-text">Loading chart data...</span>
            </div>
          </div>
        )}
        <div ref={chartRef} id={chartID} style={{ width: '100%', height: '100%' }}></div>
      </div>

      <div className="chart-instructions">
        <div className="instructions-content">
          <div className="instruction-group">
            <strong>Navigation:</strong> Drag to zoom • Double-click to reset • Mouse wheel to zoom
          </div>
          <div className="instruction-group">
            <strong>Interaction:</strong> Hover over data points for details • Use search to find datasets
          </div>
          <div className="instruction-group">
            <strong>Performance:</strong> Enable data reduction for better performance with large datasets
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TestChart;